import { useEffect, useState } from "react";
import { api, formatINR, formatApiError } from "@/lib/api";
import { useLiveRefresh } from "@/lib/live";
import { PageHeader } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Link } from "react-router-dom";
import { Plus, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";

const TYPES = ["Printing", "Inspection", "Slitting"];

export default function Machines() {
  const [machines, setMachines] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/machines").then(({ data }) => setMachines(data));
  useEffect(() => { load(); }, []);
  useLiveRefresh("masters", load);

  const del = async (m) => {
    if (!window.confirm(`Delete machine "${m.name}" (${m.code})?\n\nThis cannot be undone. Historical production logs will keep their machine name for reference.`)) return;
    try {
      await api.delete(`/machines/${m.id}`);
      toast.success("Machine deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div>
      <PageHeader
        subtitle="Production Assets · Master"
        title="Machines"
        actions={
          <div className="flex items-center gap-2">
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-machine-btn" className="rounded-sm bg-[#FF5A00] hover:bg-[#e04f00]">
                <Plus size={16} className="mr-1.5" />Add Machine
              </Button>
            </DialogTrigger>
            <MachineForm onDone={() => { setOpen(false); load(); }} />
          </Dialog>
          </div>
        }
      />
      <div className="p-8 space-y-3">
        <p className="text-sm text-neutral-600" data-testid="machines-about">
          The machines jobs run on. Presses, AVT, slitters, rewinders, the flatbed and the core cutter come from the{" "}
          <Link to="/equipment" className="underline">Equipment</Link> page on their own, with the same code and name: add or rename them there.
          Here you set what a run needs: capacity per hour (machine efficiency), hourly rate (job costing) and status.
        </p>
        <div className="border border-neutral-200 bg-white overflow-x-auto">
          <table className="w-full text-sm" data-testid="machines-table">
            <thead className="bg-neutral-50 label-caps">
              <tr>
                <th className="text-left px-4 py-2.5">Name</th>
                <th className="text-left px-4 py-2.5">Type</th>
                <th className="text-left px-4 py-2.5">Code</th>
                <th className="text-left px-4 py-2.5">Status</th>
                <th className="text-right px-4 py-2.5">Capacity / Hr</th>
                <th className="text-right px-4 py-2.5">Hourly Rate</th>
                <th className="text-left px-4 py-2.5">Description</th>
                <th className="text-right px-4 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {machines.map((m) => (
                <tr key={m.id} className="border-t border-neutral-100 data-row" data-testid={`machine-row-${m.code}`}>
                  <td className="px-4 py-2.5 font-medium">
                    {m.name}
                    {m.equipment_id && <span className="ml-2 text-[10px] uppercase tracking-widest text-neutral-400" title="Named on the Equipment page">equipment list</span>}
                  </td>
                  <td className="px-4 py-2.5 text-neutral-600 capitalize">{m.type}</td>
                  <td className="px-4 py-2.5 font-mono-num text-xs text-neutral-500">{m.code}</td>
                  <td className="px-4 py-2.5">
                    <span className={`text-xs px-2 py-0.5 rounded-sm uppercase tracking-widest ${
                      m.status === "active" ? "bg-emerald-100 text-emerald-800" :
                      m.status === "maintenance" ? "bg-amber-100 text-amber-800" :
                      "bg-neutral-200 text-neutral-700"
                    }`}>{m.status}</span>
                  </td>
                  <td className="px-4 py-2.5 text-right font-mono-num">{m.capacity_per_hour?.toLocaleString("en-IN") || "—"}</td>
                  <td className="px-4 py-2.5 text-right font-mono-num">{formatINR(m.hourly_rate)}</td>
                  <td className="px-4 py-2.5 text-xs text-neutral-600 max-w-xs truncate">{m.description || "—"}</td>
                  <td className="px-4 py-2.5 text-right whitespace-nowrap">
                    <button onClick={() => setEditing(m)} data-testid={`edit-machine-${m.code}`} className="text-neutral-400 hover:text-neutral-900 transition-colors mr-3" title="Edit run settings">
                      <Pencil size={14} />
                    </button>
                    <button
                      onClick={() => del(m)}
                      data-testid={`del-machine-${m.code}`}
                      className="text-neutral-400 hover:text-rose-700 transition-colors"
                      title="Delete machine"
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
              {machines.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-neutral-400">No machines yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        {editing && <MachineEditor m={editing} onDone={() => { setEditing(null); load(); }} />}
      </Dialog>
    </div>
  );
}

/** Run settings of a machine; a machine from the Equipment list keeps its name and code from there. */
function MachineEditor({ m, onDone }) {
  const [f, setF] = useState({ name: m.name, type: m.type, status: m.status || "active", capacity_per_hour: m.capacity_per_hour ?? 0,
    hourly_rate: m.hourly_rate ?? 0, description: m.description || "" });
  const linked = !!m.equipment_id;
  const submit = async (e) => {
    e.preventDefault();
    try {
      const body = { status: f.status, capacity_per_hour: Number(f.capacity_per_hour) || 0, hourly_rate: Number(f.hourly_rate) || 0, description: f.description };
      if (!linked) Object.assign(body, { name: f.name, type: f.type });
      await api.patch(`/machines/${m.id}`, body);
      toast.success("Machine saved"); onDone();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <DialogContent className="rounded-sm" data-testid="machine-editor">
      <DialogHeader><DialogTitle className="font-display">{m.code} · {m.name}</DialogTitle></DialogHeader>
      <form onSubmit={submit} className="grid grid-cols-2 gap-3">
        {linked ? (
          <p className="col-span-2 text-xs text-neutral-500">Name and code come from the <Link to="/equipment" className="underline">Equipment</Link> page.</p>
        ) : (
          <>
            <div className="col-span-2">
              <label className="label-caps block mb-1.5">Machine Name</label>
              <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="rounded-sm" required />
            </div>
            <div>
              <label className="label-caps block mb-1.5">Machine Type</label>
              <Select value={f.type} onValueChange={(v) => setF({ ...f, type: v })}>
                <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>{[...new Set([...TYPES, f.type].filter(Boolean))].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </>
        )}
        <div>
          <label className="label-caps block mb-1.5">Status</label>
          <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
            <SelectTrigger className="rounded-sm" data-testid="machine-edit-status"><SelectValue /></SelectTrigger>
            <SelectContent>{["active", "maintenance", "idle"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <label className="label-caps block mb-1.5">Capacity / Hr</label>
          <Input data-testid="machine-edit-capacity" type="number" step="0.01" value={f.capacity_per_hour} onChange={(e) => setF({ ...f, capacity_per_hour: e.target.value })} className="rounded-sm" />
        </div>
        <div>
          <label className="label-caps block mb-1.5">Hourly Rate (₹)</label>
          <Input data-testid="machine-edit-rate" type="number" step="0.01" value={f.hourly_rate} onChange={(e) => setF({ ...f, hourly_rate: e.target.value })} className="rounded-sm" />
        </div>
        <div className="col-span-2">
          <label className="label-caps block mb-1.5">Description</label>
          <Textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="rounded-sm" rows={2} />
        </div>
        <DialogFooter className="col-span-2">
          <Button data-testid="machine-edit-save" type="submit" className="rounded-sm bg-[#FF5A00] hover:bg-[#e04f00]">Save</Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

function MachineForm({ onDone }) {
  const [f, setF] = useState({ code: "", name: "", type: "Printing", status: "active", hourly_rate: 0, capacity_per_hour: 0, description: "" });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/machines", { ...f, hourly_rate: Number(f.hourly_rate), capacity_per_hour: Number(f.capacity_per_hour) });
      toast.success("Machine added"); onDone();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <DialogContent className="rounded-sm">
      <DialogHeader><DialogTitle className="font-display">New Machine</DialogTitle></DialogHeader>
      <p className="text-xs text-neutral-500 -mt-2">Only for a machine that is not on the Equipment page: presses, AVT, slitters and rewinders there are added on their own.</p>
      <form onSubmit={submit} className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <label className="label-caps block mb-1.5">Machine Name *</label>
          <Input data-testid="machine-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="rounded-sm" required placeholder="e.g. Nilpeter FA-4 · HP Indigo 6900 · AVT Helios" />
        </div>
        <div>
          <label className="label-caps block mb-1.5">Machine Type *</label>
          <Select value={f.type} onValueChange={(v) => setF({ ...f, type: v })}>
            <SelectTrigger data-testid="machine-type" className="rounded-sm"><SelectValue /></SelectTrigger>
            <SelectContent>{TYPES.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <label className="label-caps block mb-1.5">Code *</label>
          <Input data-testid="machine-code" value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} className="rounded-sm" required placeholder="Unique short ID e.g. FLEXO-1" />
        </div>
        <div>
          <label className="label-caps block mb-1.5">Status</label>
          <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
            <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
            <SelectContent>{["active", "maintenance", "idle"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <label className="label-caps block mb-1.5">Capacity / Hr</label>
          <Input data-testid="machine-capacity" type="number" step="0.01" placeholder="e.g. 5000 labels" value={f.capacity_per_hour} onChange={(e) => setF({ ...f, capacity_per_hour: e.target.value })} className="rounded-sm" />
        </div>
        <div>
          <label className="label-caps block mb-1.5">Hourly Rate (₹)</label>
          <Input type="number" step="0.01" value={f.hourly_rate} onChange={(e) => setF({ ...f, hourly_rate: e.target.value })} className="rounded-sm" />
        </div>
        <div className="col-span-2">
          <label className="label-caps block mb-1.5">Description</label>
          <Textarea data-testid="machine-desc" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="rounded-sm" rows={2} placeholder="Optional notes about the machine" />
        </div>
        <DialogFooter className="col-span-2">
          <Button data-testid="machine-submit" type="submit" className="rounded-sm bg-[#FF5A00] hover:bg-[#e04f00]">Add</Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}
